import { useState } from "react";
import { Link } from "react-router-dom";

import { useAuth, useToast } from "../App";
import { api } from "../lib/api";
import { useReveal } from "../lib/useReveal";

const STEPS = [
  {
    n: "1",
    title: "Upload",
    body: "PDFs, Word docs, Markdown, lecture recordings, diagrams — whatever your course actually gives you.",
  },
  {
    n: "2",
    title: "Understand",
    body: "Every page and paragraph is read, structured, and indexed — page numbers and timestamps kept intact.",
  },
  {
    n: "3",
    title: "Generate",
    body: "Cards are written from specific passages, not guessed from a summary — each one cites where it came from.",
  },
  {
    n: "4",
    title: "Review",
    body: "Every card is checked against its source before you see it. Accept, edit, or reject — nothing ships blind.",
  },
  {
    n: "5",
    title: "Export",
    body: "A real .apkg file. Basic, cloze, and image occlusion. Import it into Anki and it just works.",
  },
];

export default function LandingPage() {
  const { pushError } = useToast();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const passwordsMismatch =
    mode === "signup" && confirmPassword.length > 0 && password !== confirmPassword;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password || busy) return;
    if (mode === "signup" && password !== confirmPassword) {
      pushError(new Error("Those passwords don't match."));
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") await api.signup(email.trim(), password);
      else await api.login(email.trim(), password);
      await refresh(); // Home re-renders as the dashboard once auth state updates
    } catch (error) {
      pushError(error);
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next: "signup" | "login") => {
    setMode(next);
    setConfirmPassword("");
  };

  return (
    <main>
      {/* -------------------------------------------------- hero */}
      <section className="hero">
        <div className="hero-field" aria-hidden="true">
          <div className="hero-grid-lines" />
          <div className="hero-glow" />
          <div className="hero-shape hero-shape-a" />
          <div className="hero-shape hero-shape-b" />
        </div>

        <div className="page hero-content" style={{ paddingTop: 64, paddingBottom: 56 }}>
          <div className="grid grid-2" style={{ alignItems: "center", gap: 40 }}>
            <div>
              <h1
                className="hero-title fade-up"
                style={{ fontSize: 38, lineHeight: 1.15, marginBottom: 16, letterSpacing: "-0.02em" }}
              >
                Upload your lecture notes.
                <br />
                Get flashcards with the page number attached.
              </h1>
              <p
                className="hero-sub fade-up"
                style={{
                  fontSize: 16,
                  color: "var(--text-muted)",
                  marginBottom: 26,
                  maxWidth: 480,
                  animationDelay: "0.08s",
                }}
              >
                MakeitAnki reads your PDFs, slides, and recordings, writes flashcards grounded in
                specific passages, and lets you review every one before anything is exported. Then
                it builds a real Anki deck — no CSV, no manual re-formatting.
              </p>

              <div className="fade-up" style={{ animationDelay: "0.16s" }}>
                <div className="segmented" style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    aria-pressed={mode === "signup"}
                    onClick={() => switchMode("signup")}
                  >
                    Sign up
                  </button>
                  <button type="button" aria-pressed={mode === "login"} onClick={() => switchMode("login")}>
                    Log in
                  </button>
                </div>

                <form onSubmit={submit} className="stack" style={{ maxWidth: 340, gap: 8 }}>
                  <input
                    type="email"
                    required
                    placeholder="you@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  <input
                    type="password"
                    required
                    minLength={mode === "signup" ? 8 : undefined}
                    placeholder={mode === "signup" ? "Password (at least 8 characters)" : "Password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  />
                  {mode === "signup" && (
                    <input
                      type="password"
                      required
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      aria-invalid={passwordsMismatch}
                      style={passwordsMismatch ? { borderColor: "var(--bad)" } : undefined}
                    />
                  )}
                  {passwordsMismatch && (
                    <span className="small" style={{ color: "var(--bad)" }}>
                      Passwords don't match.
                    </span>
                  )}
                  <button
                    type="submit"
                    className="btn-primary btn-lg"
                    disabled={busy || passwordsMismatch}
                  >
                    {busy ? "…" : mode === "signup" ? "Create account" : "Log in"}
                  </button>
                </form>
                <p className="small faint" style={{ marginTop: 10 }}>
                  {mode === "signup" ? (
                    <>
                      Already have an account?{" "}
                      <button type="button" className="btn-ghost btn-sm" onClick={() => switchMode("login")}>
                        Log in
                      </button>
                    </>
                  ) : (
                    <>
                      New here?{" "}
                      <button type="button" className="btn-ghost btn-sm" onClick={() => switchMode("signup")}>
                        Create an account
                      </button>
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="panel fade-up" aria-hidden="true" style={{ animationDelay: "0.24s" }}>
              <div className="row" style={{ marginBottom: 10 }}>
                <span className="chip chip-outline">Cloze</span>
                <span className="chip chip-good" style={{ marginLeft: "auto" }}>
                  92% confidence
                </span>
              </div>
              <div className="qa-line">
                <span className="qa-label">Card</span>
                <div className="qa-text">
                  The mitochondrion produces ATP through{" "}
                  <span className="cloze-gap">oxidative phosphorylation</span>.
                </div>
              </div>
              <hr className="divider" />
              <button
                className="source-badge"
                type="button"
                style={{ cursor: "default" }}
                tabIndex={-1}
              >
                📄 Biology Lecture 4.pdf · p.27
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------- how it works */}
      <section style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="page">
          <h2 style={{ marginBottom: 20 }}>How it works</h2>
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {STEPS.map((step, i) => (
              <StepCard key={step.n} step={step} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------- pricing teaser */}
      <section className="page center" style={{ paddingTop: 40 }}>
        <p className="muted">
          Free to start. <Link to="/pricing">See plans →</Link>
        </p>
      </section>
    </main>
  );
}

function StepCard({ step, index }: { step: (typeof STEPS)[number]; index: number }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`reveal${visible ? " is-visible" : ""}`}
      style={{ transitionDelay: visible ? `${index * 70}ms` : undefined }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: "var(--accent-soft)",
          color: "var(--accent)",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          marginBottom: 10,
        }}
      >
        {step.n}
      </div>
      <h3 style={{ marginBottom: 4 }}>{step.title}</h3>
      <p className="small muted" style={{ margin: 0 }}>
        {step.body}
      </p>
    </div>
  );
}
